import { useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPanel,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  Skeleton,
  Text,
} from "@salestrak/ui";

import type { Workspace } from "#app/lib/data/workspace-context";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useLeaveWorkspace } from "#app/lib/hooks/use-leave-workspace";
import { WORKSPACE_DELETION_GRACE_DAYS } from "#app/lib/hooks/use-workspace-deletion";
import { DeleteWorkspaceDialog } from "#app/components/workspace-deletion/delete-workspace-dialog";

/**
 * THE leave-workspace confirmation, opened from the profile danger zone and
 * the pending-deletion screen. Three shapes, decided by the server's
 * eligibility read rather than guessed client-side:
 *
 *   may leave        → what goes and what stays, then one destructive press.
 *   sole Owner       → every workspace keeps an Owner: hand the role on
 *                      (Members) or delete the workspace instead.
 *   last member      → nobody left to reopen it and nothing ever purges it:
 *                      delete the workspace instead; an Owner can, from here.
 *
 * Leaving is reversible by a fresh invite, so there is no typed
 * confirmation — deletion, which is not, keeps its own. Renders open; the
 * host unmounts it when `onOpenChange(false)` fires.
 */
export function LeaveWorkspaceDialog({
  workspace,
  onOpenChange,
}: {
  workspace: Pick<Workspace, "workspaceId" | "name" | "deletion">;
  onOpenChange: (open: boolean) => void;
}) {
  const { push } = useAppNavigate();
  const { eligibility, eligibilityLoading, leave, leaving } = useLeaveWorkspace(workspace);
  const [deleting, setDeleting] = useState(false);
  const frozen = !!workspace.deletion;

  if (deleting) {
    return (
      <DeleteWorkspaceDialog
        workspace={workspace}
        onOpenChange={(open) => {
          if (!open) onOpenChange(false);
        }}
      />
    );
  }

  const blocker = eligibility?.blocker ?? null;
  const canLeave = eligibility?.canLeave ?? false;
  const hasSomeoneToHandTo =
    blocker === "LAST_OWNER" && (eligibility?.otherActiveMemberCount ?? 0) > 0;
  const viewerIsOwner = eligibility?.viewerIsOwner ?? false;

  let title = `Leave ${workspace.name}?`;
  let description = `You'll lose access to ${workspace.name}. The Projects, comments and history you created stay behind with your name on them, and an admin can invite you back.`;
  if (blocker === "LAST_OWNER") {
    title = "You're the only Owner";
    description = hasSomeoneToHandTo
      ? "Every workspace keeps at least one Owner. Make another member an Owner first, or delete the workspace instead."
      : "You're the only Owner and the only member. A workspace nobody can open is never cleaned up, so delete it instead.";
  } else if (blocker === "LAST_MEMBER") {
    title = "You're the last member";
    description = viewerIsOwner
      ? "A workspace nobody can open is never cleaned up, so delete it instead."
      : "A workspace nobody can open is never cleaned up, and no Owner is left to delete it. Contact SalesTrak support.";
  }

  const deleteDoor = (
    <Button
      disabled={eligibilityLoading}
      onClick={() => setDeleting(true)}
      color={hasSomeoneToHandTo ? "neutral" : "destructive"}
      variant={hasSomeoneToHandTo ? "outline" : "solid"}
    >
      Delete workspace...
    </Button>
  );

  return (
    <AlertDialog open onOpenChange={onOpenChange}>
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>{eligibilityLoading ? `Leave ${workspace.name}?` : title}</AlertDialogTitle>
          <AlertDialogDescription>
            {eligibilityLoading ? "Checking who else is here…" : description}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {eligibilityLoading ? (
          <AlertDialogPanel>
            <Box gap="xs">
              <Skeleton delayed height={14} width="70%" />
              <Skeleton delayed height={14} width="45%" />
            </Box>
          </AlertDialogPanel>
        ) : blocker && viewerIsOwner && !frozen ? (
          <AlertDialogPanel>
            <Text size="sm" variant="muted">
              Deleting freezes the workspace right away and removes it for good
              after {WORKSPACE_DELETION_GRACE_DAYS} days. Any Owner can restore it
              until then.
            </Text>
          </AlertDialogPanel>
        ) : null}

        <AlertDialogFooter>
          <AlertDialogClose disabled={leaving} render={<Button variant="outline" />}>
            Cancel
          </AlertDialogClose>
          {canLeave || eligibilityLoading ? (
            <Button
              disabled={eligibilityLoading || leaving}
              loading={leaving}
              onClick={() => void leave()}
              color="destructive"
            >
              Leave workspace
            </Button>
          ) : null}
          {blocker === "LAST_OWNER" && hasSomeoneToHandTo && !frozen ? (
            <Button
              onClick={() => {
                onOpenChange(false);
                push("/settings/members");
              }}
            >
              Manage members
            </Button>
          ) : null}
          {blocker && viewerIsOwner && !frozen ? deleteDoor : null}
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
